import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from 'react';
import { selectUserProfile } from "../../../store/slices/userSlice";
import { clearCaseCreation, selectCaseCreation } from "../../../store/slices/chatContextSlice";

export const useCaseForm = (option: string, suboption: string, caseSubOption: any) => {
    const { t } = useTranslation("cases");
    const user = useSelector(selectUserProfile);
    const caseCreation = useSelector(selectCaseCreation);
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    // Capture Redux values on first render so they survive Strict Mode double-renders
    const initialValuesRef = useRef({
        description: caseCreation.caseDescription,
        deflectionResponse: caseCreation.deflectionResponse,
    });

    // Clear Redux state after mount instead of during render
    useEffect(() => {
        if (initialValuesRef.current.description || initialValuesRef.current.deflectionResponse) {
            dispatch(clearCaseCreation());
        }
    }, [dispatch]);

    // Parse query string parameters
    const getQueryParam = (key: string): string => {
        return searchParams.get(key) || '';
    };

    const schema = yup.object().shape({
        option: yup.string(),
        suboption: yup.string(),
        firstName: yup.string().required(t('Required')),
        lastName: yup.string().required(t('Required')),
        workEmail: yup.string().email(t('Not an email')).required(t('Required')),
        contactMethod: yup.string(),
        telephone: yup.string().when('contactMethod', {
            is: 'phone',
            then: (schema) => yup.string().required('Required'),
            otherwise: (schema) => schema,
        }),
        country: yup.string().required(t('Required')),
        stateOrProvince: yup.string(),
        zipCode: yup.string(),
        city: yup.string(),
        description: yup.string().required(t('Required')),
        orderOrPoNumber: yup.string(),
        reasonForReturn: yup.string(),
        organisationType: caseSubOption?.organizationTypeRequired ? yup.string().required(t('Required')) : yup.string(),
        role: yup.string(),
        productName: caseSubOption?.productNameRequired ? yup.string().required(t('Required')) : yup.string(),
        productLineCode: yup.string(),
        productSerial: yup.string(),
        accountNumber: caseSubOption?.accountNumberRequired ? yup.string().required(t('Required')) : yup.string(),
        privacyStatement: yup.boolean(),
        organization: yup.string(),
        llpOrganizationID: caseSubOption?.llpOrganizationIDVisible ? yup.string().required(t('Required')) : yup.string(),
        product: caseSubOption?.productVisible ? yup.string().required(t('Required')) : yup.string(),
        deflectionResponse: yup.string(),
    });

    const formMethods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            option: option,
            suboption: suboption,
            description: initialValuesRef.current.description || getQueryParam('description') || '',
            firstName: user?.firstName || getQueryParam('firstName') || '',
            lastName: user?.lastName || getQueryParam('lastName') || '',
            workEmail: user?.email || getQueryParam('workEmail') || '',
            telephone: getQueryParam('telephone') || '',
            contactMethod: getQueryParam('contactMethod') || '',
            country: user?.currentOrganization?.address?.country?.codeAlpha2 || getQueryParam('country').toUpperCase() || '',
            stateOrProvince: user?.currentOrganization?.address?.state || getQueryParam('stateOrProvince') || '',
            zipCode: user?.currentOrganization?.address?.zipCode || getQueryParam('zipCode') || '',
            city: user?.currentOrganization?.address?.city || getQueryParam('city') || '',
            productName: getQueryParam('productName') || '',
            productLineCode: getQueryParam('productLineCode') || '',
            accountNumber: user?.currentOrganization?.customerNo || getQueryParam('accountNumber') || '',
            reasonForReturn: getQueryParam('reasonForReturn') || '',
            organisationType: getQueryParam('organisationType') || '',
            role: getQueryParam('role') || '',
            privacyStatement: getQueryParam('privacyStatement') === 'true' ? true : true,
            orderOrPoNumber: getQueryParam('orderOrPoNumber') || '',
            productSerial: getQueryParam('productSerial') || '',
            product: getQueryParam('product') || '',
            organization: user?.currentOrganization?.name || getQueryParam('organization') || '',
            llpOrganizationID: getQueryParam('llpOrganizationID') || '',
            deflectionResponse: initialValuesRef.current.deflectionResponse || '',
        }
    });

    return formMethods;
};
